import React, { useState, useEffect } from 'react'
import classnames from 'classnames'
import map from 'lodash/map'
import filter from 'lodash/filter'
import SiteCheckbox from '@SiteComponents/SiteCheckbox'
import Add from '@Images/labels_editor_add.svg'

import {
  ILabel,
} from '@SiteModules/Label/types'

import './LabelsManager.scss'

const LABEL_CAPTION_MAX_LENGTH = 100 // characters

interface LabelsManagerProps {
  readonly labels: ILabel[]
  readonly selectedLabelIds: number[]
  readonly selectedProjectTitles: string[]
  readonly indeterminateLabelIds: number[]
  readonly isForSelectedProjects?: boolean
  createLabel(caption: string): void
  changeSelectedLabels(e: React.ChangeEvent<HTMLInputElement>, labelId: number, shouldCloseLabelsManager: boolean): void
}

const LabelsManager: React.FC<LabelsManagerProps> = props => {
  const {
    labels,
    selectedLabelIds,
    indeterminateLabelIds,
    selectedProjectTitles,
    isForSelectedProjects,
  } = props

  const [labelSearchText, setLabelSearchText] = useState('')
  const [relevantLabels, setRelevantLabels] = useState(labels)

  const hasNoRelevantLabels = relevantLabels.length === 0

  const [showCheckbox, setShowCheckbox] = useState<null | number>(hasNoRelevantLabels ? null : relevantLabels[0].id)

  const noLabelWithSameCaption = !map(relevantLabels, relevantLabel => relevantLabel.attributes.caption).includes(labelSearchText)
  const labelSearchTextLongEnough = labelSearchText.length > 1
  const labelSearchTextShortEnough = labelSearchText.length <= LABEL_CAPTION_MAX_LENGTH
  const shouldAllowToCreateLabel = noLabelWithSameCaption && labelSearchTextLongEnough && labelSearchTextShortEnough
  const numberOfSelectedProjects = selectedProjectTitles.length
  const shouldShowDropdown = shouldAllowToCreateLabel || relevantLabels.length > 0

  useEffect(() => {
    const newRelevantLabels = filter(labels, label => label.attributes.caption.toLowerCase().includes(labelSearchText.toLowerCase()))
    setRelevantLabels(newRelevantLabels)
  }, [labels, labelSearchText])

  useEffect(() => {
    if (typeof(showCheckbox) !== 'number' && relevantLabels.length > 0) {
      setShowCheckbox(relevantLabels[0].id)
    }
  }, [relevantLabels.length])

  const handleLabelSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = (e.target as HTMLInputElement).value
    setLabelSearchText(searchText)
  }

  const handleCreateLabel = () => {
    props.createLabel(labelSearchText)
  }

  const labelItems = map(relevantLabels, (label) => {
    const { id, attributes } = label
    const { caption, projectIds } = attributes

    const numberOfProjectsUsed = projectIds.length
    const selected = selectedLabelIds.includes(id)
    const indeterminate = indeterminateLabelIds.includes(id)
    const shouldShowCheckbox = selected || indeterminate || showCheckbox === id
    let checkboxValue = 'unchecked'
    if (indeterminate) {
      checkboxValue = 'indeterminate'
    } else if (selected) {
      checkboxValue = 'checked'
    }

    const displayCheckbox = () => {
      setShowCheckbox(id)
    }

    const handleLabelSelection = (e: React.ChangeEvent<HTMLInputElement>, clickOnCheckmark: boolean) => {
      const shouldCloseLabelsManager = !!isForSelectedProjects && !clickOnCheckmark
      props.changeSelectedLabels(e, id, shouldCloseLabelsManager)
    }

    return (
      <li
        key={id}
        className={classnames(
          'LabelsManager__dropdown-item',
          { 'LabelsManager__dropdown-item-show-checkbox': showCheckbox === id }
        )}
        onMouseEnter={displayCheckbox}
      >
        {shouldShowCheckbox && (
          <SiteCheckbox
            id={id}
            checkboxValue={checkboxValue as 'checked' | 'unchecked' | 'indeterminate'}
            onChange={handleLabelSelection}
          />
        )}
        <label
          className="LabelsManager__dropdown-item-label"
          htmlFor={`label_${id}`}
        >
          <div className="LabelsManager__dropdown-item-caption">
            {caption}
          </div>
          {!isForSelectedProjects && (
            <div className="LabelsManager__dropdown-item-projects-used">
              {numberOfProjectsUsed === 1 ? '1 project' : `${numberOfProjectsUsed} projects`}
            </div>
          )}
        </label>
      </li>
    )
  })

  const renderCreateNewLabel = () => {
    return (
      <li
        className="LabelsManager__dropdown-item LabelsManager__dropdown-new"
        onClick={handleCreateLabel}
      >
        <Add />
        Create a new label:
        <span className="LabelsManager__dropdown-new-caption">
          {labelSearchText}
        </span>
      </li>
    )
  }

  return (
    <div
      className={classnames(
        'LabelsManager',
        { 'LabelsManager__for-selected-projects': isForSelectedProjects }
      )}
    >
      <div className={classnames(
        'LabelsManager__input-wrapper',
        { 'LabelsManager__input-wrapper-labels': !hasNoRelevantLabels },
        { 'LabelsManager__input-wrapper-no-labels': hasNoRelevantLabels }
      )}>
        {isForSelectedProjects && (
          <div className="LabelsManager__selected">
            {numberOfSelectedProjects === 1 ? (
              <React.Fragment>
                <span className="LabelsManager__selected-title">
                  {selectedProjectTitles[0]}
                </span>
                <span>{'\u00A0'}</span>
                <span>
                  selected
                </span>
              </React.Fragment>
            ) : (
              `${numberOfSelectedProjects} projects selected`
            )}
          </div>
        )}
        <input
          placeholder="Select..."
          autoComplete="off"
          value={labelSearchText}
          onChange={handleLabelSearchTextChange}
        />
      </div>
      {shouldShowDropdown && (
        <ul className="LabelsManager__dropdown-items">
          {labelItems}
          {shouldAllowToCreateLabel && (
            renderCreateNewLabel()
          )}
        </ul>
      )}
    </div>
  )
}

export default LabelsManager
